"use client"

import { useState, useRef, useEffect, useCallback } from "react"
import { Worker, Viewer, SpecialZoomLevel } from "@react-pdf-viewer/core"
import { searchPlugin } from "@react-pdf-viewer/search"
import { ArrowLeft, Download, FileIcon, Type, ImageIcon, FilePenLine, Save, Palette } from "lucide-react"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"
import "@react-pdf-viewer/search/lib/styles/index.css"
import "../assets/styles/EditMyReport.css"
import en_US from "@react-pdf-viewer/locales/lib/en_US.json"
import es_ES from "@react-pdf-viewer/locales/lib/es_ES.json"
import { useTranslation } from "react-i18next"

// Worker de PDF.js para react-pdf-viewer
const pdfWorkerUrl = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js"

// Colores predefinidos para títulos
const PREDEFINED_COLORS = [
  { name: "Negro", value: "#000000" },
  { name: "Azul", value: "#1890ff" },
  { name: "Verde", value: "#52c41a" },
  { name: "Rojo", value: "#ff4d4f" },
  { name: "Púrpura", value: "#722ed1" },
  { name: "Naranja", value: "#fa8c16" },
  { name: "Gris", value: "#8c8c8c" },
  { name: "Dorado", value: "#faad14" },
]

export default function PdfViewer() {
  const searchPluginInstance = searchPlugin()
  const pdfContainerRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [titleColor, setTitleColor] = useState("#000000")
  const [lastPageColor, setLastPageColor] = useState("#000000")
  const [fileUrl] = useState(process.env.PUBLIC_URL + "/prueba.pdf")
  const [activeTab, setActiveTab] = useState("titles")
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentSignature, setCurrentSignature] = useState(null)
  const [selectedTool, setSelectedTool] = useState(null)
  const { i18n } = useTranslation()
  const currentLang = i18n.language

  const pdfLocales = {
    es: es_ES,
    en: en_US,
  }

  const localization = pdfLocales[currentLang] || en_US

  const [textReplacements] = useState([
    {
      id: 1,
      searchText: "Informe Radiográfico",
      replacementText: "Informe Radiográfico",
      page: 2,
      found: false,
      position: null,
    },
    {
      id: 2,
      searchText: "DIAGNÓSTICO RADIOGRÁFICO",
      replacementText: "DIAGNÓSTICO RADIOGRÁFICO",
      page: 2,
      found: false,
      position: null,
    },
  ])

  const [logoPreview, setLogoPreview] = useState(null)

  const handleBack = () => console.log("Going back")
  const handleDownload = () => alert("PDF download started")
  const handleSave = async () => {
    try {
      alert("PDF saved successfully with modifications")
    } catch (error) {
      console.error("Error saving PDF:", error)
      alert("Error saving PDF")
    }
  }

  const handleLogoUpload = (event) => {
    const file = event.target.files[0]
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onload = (e) => setLogoPreview(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  // Función para manejar el zoom del PDF
  const handleZoom = (e) => {
    setScale(e.scale)
    // Re-buscar texto cuando cambia el zoom
    setTimeout(() => {
      findAndReplaceText()
    }, 300)
  }

  // Función para manejar la carga del documento
  const handleDocumentLoad = (e) => {
    console.log("PDF document loaded")
    // Buscar texto después de cargar el documento
    setTimeout(() => {
      findAndReplaceText()
    }, 1000)
  }

  // Función para manejar el cambio de página
  const handlePageChange = (e) => {
    setCurrentPage(e.currentPage)
    // Re-buscar texto cuando cambia la página
    setTimeout(() => {
      findAndReplaceText()
    }, 300)
  }

  // Función para seleccionar color predefinido
  const handlePredefinedColorSelect = (color) => {
    setTitleColor(color)
  }

  const findTextInPDF = useCallback((searchText, targetPage, callback) => {
    const textLayers = document.querySelectorAll(".rpv-core__text-layer")

    textLayers.forEach((textLayer, pageIndex) => {
      const pageNumber = pageIndex + 1
      if (targetPage && pageNumber !== targetPage) return

      const textElements = textLayer.querySelectorAll(".rpv-core__text-layer-text")

      textElements.forEach((element) => {
        const textContent = element.textContent || ""
        if (textContent.includes(searchText)) {
          const rect = element.getBoundingClientRect()
          const layerRect = textLayer.getBoundingClientRect()

          const position = {
            x: rect.left - layerRect.left,
            y: rect.top - layerRect.top,
            width: rect.width,
            height: rect.height,
            element: element,
            textLayer: textLayer,
            pageNumber: pageNumber,
          }

          callback([position])
        }
      })
    })
  }, [])

  const hideOriginalText = useCallback((position) => {
    if (position.element) {
      // Crear un overlay para ocultar el texto original
      const overlay = document.createElement("div")
      overlay.className = "pdf-text-overlay"
      overlay.style.cssText = `
        position: absolute;
        left: ${position.x}px;
        top: ${position.y}px;
        width: ${position.width}px;
        height: ${position.height}px;
        background-color: white;
        z-index: 9;
        pointer-events: none;
      `
      position.textLayer.appendChild(overlay)
    }
  }, [])

  const createReplacementText = useCallback(
    (text, position) => {
      if (!position.element) return

      // Obtener el estilo computado del elemento original
      const originalStyle = window.getComputedStyle(position.element)

      // Crear el elemento de reemplazo
      const replacementElement = document.createElement("div")
      replacementElement.className = "pdf-text-replacement"
      replacementElement.textContent = text

      // Copiar todas las propiedades de estilo relevantes del texto original
      const stylesToCopy = [
        "font-family",
        "font-size",
        "font-weight",
        "font-style",
        "letter-spacing",
        "word-spacing",
        "line-height",
        "text-transform",
        "white-space",
        "text-align",
      ]

      // Construir el CSS para el elemento de reemplazo
      let cssText = `
        position: absolute;
        left: ${position.x}px;
        top: ${position.y}px;
        width: ${position.width}px;
        height: ${position.height}px;
        color: ${titleColor};
        z-index: 10;
        pointer-events: none;
        text-decoration: underline;
        text-decoration-color: ${titleColor};
        text-underline-offset: 2px;
      `

      // Añadir los estilos copiados del original
      stylesToCopy.forEach((prop) => {
        cssText += `${prop}: ${originalStyle.getPropertyValue(prop)};`
      })

      replacementElement.style.cssText = cssText

      // Añadir el elemento al layer
      position.textLayer.appendChild(replacementElement)
    },
    [titleColor],
  )

  const findAndReplaceText = useCallback(() => {
    // Limpiar elementos existentes
    document.querySelectorAll(".pdf-text-replacement").forEach((el) => el.remove())
    document.querySelectorAll(".pdf-text-overlay").forEach((el) => el.remove())

    textReplacements.forEach((replacement) => {
      findTextInPDF(replacement.searchText, replacement.page, (positions) => {
        if (positions && positions.length > 0) {
          positions.forEach((pos) => {
            // Ocultar el texto original
            hideOriginalText(pos)
            // Crear el texto de reemplazo
            createReplacementText(replacement.replacementText, pos)
          })
        }
      })
    })
  }, [textReplacements, findTextInPDF, hideOriginalText, createReplacementText])

  const handleSignatureStart = (e) => {
    if (selectedTool !== "signature") return

    const rect = pdfContainerRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale
    const y = (e.clientY - rect.top) / scale

    setIsDrawing(true)
    setCurrentSignature({
      points: [{ x, y }],
      page: currentPage,
    })
  }

  const handleSignatureMove = (e) => {
    if (!isDrawing || !currentSignature) return

    const rect = pdfContainerRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left) / scale
    const y = (e.clientY - rect.top) / scale

    setCurrentSignature((prev) => ({
      ...prev,
      points: [...prev.points, { x, y }],
    }))
  }

  const handleSignatureEnd = () => {
    if (!isDrawing || !currentSignature) return

    setIsDrawing(false)
    setCurrentSignature(null)
  }

  const handlePdfClick = (e) => {
    if (selectedTool !== "logo" && selectedTool !== "signatures") return
    // Click handling logic
  }

  // Efecto para re-aplicar cambios cuando cambia el color
  useEffect(() => {
    if (titleColor) {
      setTimeout(() => {
        findAndReplaceText()
      }, 100)
    }
  }, [titleColor, findAndReplaceText])

  // Efecto para buscar texto cuando se carga la página
  useEffect(() => {
    const timer = setTimeout(() => {
      findAndReplaceText()
    }, 1500)

    return () => clearTimeout(timer)
  }, [currentPage, findAndReplaceText])

  return (
    <div className="pdf-editor-container">
      <header className="pdf-editor-header">
        <div className="header-left">
          <button className="back-button" onClick={handleBack}>
            <ArrowLeft size={20} />
            <span>Volver</span>
          </button>
          <h1 className="titleh1">Editar mi informe</h1>
        </div>
        <div className="header-actions">
          <button className="action-button2 save-button" onClick={handleSave}>
            <Save size={18} />
            <span>Guardar</span>
          </button>
          <button className="action-button2 download-button" onClick={handleDownload}>
            <Download size={18} />
            <span>Descargar</span>
          </button>
        </div>
      </header>

      <div className="pdf-editor-content">
        <div className="pdf-editor-toolbar">
          <div className="toolbar-tabs">
            {["titles", "logo", "signatures", "lastPage"].map((key) => {
              const config = {
                titles: { icon: <Type size={18} />, label: "Títulos" },
                logo: { icon: <ImageIcon size={18} />, label: "Logo" },
                signatures: { icon: <FilePenLine size={18} />, label: "Firmas" },
                lastPage: { icon: <FileIcon size={18} />, label: "Últ. pág." },
              }
              return (
                <button
                  key={key}
                  className={`tab-button ${activeTab === key ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(key)
                    setSelectedTool(key)
                  }}
                >
                  {config[key].icon}
                  <span>{config[key].label}</span>
                </button>
              )
            })}
          </div>

          <div className="toolbar-content">
            {activeTab === "titles" && (
              <div className="titles-container">
                <div className="color-section">
                  <div className="section-header">
                    <Palette size={18} />
                    <span>Color de títulos</span>
                  </div>

                  {/* Colores predefinidos */}
                  <div className="predefined-colors">
                    <label className="color-section-label">Colores predefinidos:</label>
                    <div className="color-grid">
                      {PREDEFINED_COLORS.map((color) => (
                        <button
                          key={color.value}
                          className={`color-button ${titleColor === color.value ? "active" : ""}`}
                          style={{ backgroundColor: color.value }}
                          onClick={() => handlePredefinedColorSelect(color.value)}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Color personalizado */}
                  <div className="custom-color">
                    <label className="color-section-label">Color personalizado:</label>
                    <div className="color-picker-container">
                      <input
                        type="color"
                        id="title-color"
                        value={titleColor}
                        onChange={(e) => setTitleColor(e.target.value)}
                        className="color-picker"
                      />
                      <div className="color-preview" style={{ backgroundColor: titleColor }}>
                        <span className="color-value">{titleColor.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Vista previa */}
                  <div className="color-preview-section">
                    <label className="color-section-label">Vista previa:</label>
                    <div
                      className="preview-text"
                      style={{ color: titleColor, textDecoration: "underline", textDecorationColor: titleColor }}
                    >
                      Ejemplo de título
                    </div>
                  </div>

                  {/* Lista de títulos */}
                  <div className="titles-list">
                    <label className="color-section-label">Textos a modificar:</label>
                    <div className="titles-items">
                      {textReplacements.map((replacement) => (
                        <div key={replacement.id} className="title-item">
                          <div className="title-color-indicator" style={{ backgroundColor: titleColor }} />
                          <span className="title-text">{replacement.searchText}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "logo" && (
              <div className="logo-upload">
                <label htmlFor="logo-upload" className="upload-label">
                  <ImageIcon size={24} />
                  <span>Subir Logo</span>
                </label>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="file-input"
                />
                {logoPreview && (
                  <>
                    <div className="logo-preview">
                      <img src={logoPreview || "/placeholder.svg"} alt="Logo preview" draggable={false} />
                    </div>
                    <p className="logo-instructions">Arrastra el logo al PDF para posicionarlo</p>
                  </>
                )}
              </div>
            )}

            {activeTab === "signatures" && (
              <div className="signature-container">
                <button className="tool-button">
                  <FilePenLine size={18} />
                  <span>Añadir Firma</span>
                </button>
                <div className="signature-canvas-placeholder">Área para firma</div>
              </div>
            )}

            {activeTab === "lastPage" && (
              <div className="color-picker-container">
                <label htmlFor="last-page-color">Color última página:</label>
                <input
                  type="color"
                  id="last-page-color"
                  value={lastPageColor}
                  onChange={(e) => setLastPageColor(e.target.value)}
                  className="color-picker"
                />
                <div className="color-preview" style={{ backgroundColor: lastPageColor }}>
                  <span className="color-value">{lastPageColor}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="pdf-preview-container"
          ref={pdfContainerRef}
          onClick={handlePdfClick}
          onMouseDown={selectedTool === "signature" ? handleSignatureStart : undefined}
          onMouseMove={selectedTool === "signature" ? handleSignatureMove : undefined}
          onMouseUp={selectedTool === "signature" ? handleSignatureEnd : undefined}
          onMouseLeave={selectedTool === "signature" ? handleSignatureEnd : undefined}
        >
          <div className="pdf-viewer-wrapper">
            <Worker workerUrl={pdfWorkerUrl}>
              <Viewer
                fileUrl={fileUrl}
                localization={localization}
                plugins={[searchPluginInstance]}
                defaultScale={SpecialZoomLevel.PageFit}
                onZoom={handleZoom}
                onDocumentLoad={handleDocumentLoad}
                onPageChange={handlePageChange}
              />
            </Worker>
          </div>
        </div>
      </div>
    </div>
  )
}
